import React, { useContext, useState, useEffect } from "react";

import {
  Stack,
  Box,
  Grid2 as Grid,
  TextField,
  Typography,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
} from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { useMutation } from "@apollo/client";

//   icon
import { BiMinusCircle } from "react-icons/bi";

//Srcs
import "../../../Style/dialogStyle.scss";
import { AuthContext } from "../../../Context/AuthContext";
import { translateLauguage } from "../../../Function/Translate";
import {
  SelectAcademicYear,
  SelectSchool,
} from "../../../Function/DynamicSelect";
import { IMPORT_STUDENT_ATTENDANCE_FROM_CSV_FILE } from "../../../Schema/Attendance";
import ImportStudentAttendanceFromCSV from "./ImportStudentAttendanceFromCSV";
import DatePickerForDialog from "../../Include/SelecteDatePicker/DatePickerForDialog";
import moment from "moment";

export default function ImportStudentAttendanceDialog({
  open,
  handleClose,
  setRefetch,
  classId,
}) {
  // ===================== Change Language ==========================
  const { language, setAlert } = useContext(AuthContext);
  const { t } = translateLauguage(language);

  const [loading, setLoading] = useState(false);

  const [csvData, setCSVData] = useState([]);

  // console.log("csvDataAtt==>", csvData);

  const [academicYearVal, setAcademicYearVal] = useState({ id: "", title: "" });

  //======================== Create User =========================
  const [importStudentAttendanceFromCSVFile] = useMutation(
    IMPORT_STUDENT_ATTENDANCE_FROM_CSV_FILE,
    {
      onCompleted: ({ importStudentAttendanceFromCSVFile }) => {
        // console.log("importStudentAttendanceFromCSVFile", importStudentAttendanceFromCSVFile);
        setLoading(false);
        if (importStudentAttendanceFromCSVFile?.is_success === true) {
          setAlert(
            true,
            "success",
            importStudentAttendanceFromCSVFile?.message
          );
          handleClose();
          resetForm();
          setCSVData([]);
          setRefetch();
        } else {
          setAlert(true, "error", importStudentAttendanceFromCSVFile?.message);
          console.log("error:::", importStudentAttendanceFromCSVFile?.message);
          handleClose();
        }
      },
      onError: (error) => {
        setLoading(false);
        console.log("error",error)
        if (error?.message?.message_kh && error?.message?.message_en) {
          setAlert(true, "error", error?.message);
        } else {
          setAlert(true, "error", {
            message_kh: error?.message,
            message_en: error?.message,
          });
        }
      },
    }
  );

  //fromik
  const CheckValidation = Yup.object().shape({
    attendanceDate: Yup.string().required(t("require")),
    classId: Yup.string(),
    student_id: Yup.string(),
    reason: Yup.string(),
    gender: Yup.string(),
    full_name_latin: Yup.string(),
    full_name_kh: Yup.string(),
    attendance_status: Yup.string(),
    attendance_date: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      attendanceDate: moment(),
      classId: "",
      student_id: "",
      gender: "",
      full_name_latin: "",
      full_name_kh: "",
      attendance_status: "",
      attendance_date: "",
      reason: "",
    },

    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      //   console.log("values:::", values);
      setLoading(true);

      const submitData = csvData?.map((e) => {
        return {
          full_name_kh: e?.KhmerName ? e?.KhmerName : null,
          full_name_latin: e?.LatinName ? e?.LatinName : null,
          gender: e?.Gender ? e?.Gender : null,
          student_id: parseInt(e?.ID),
          attendance_status: e?.Attendance ? e?.Attendance : "",
          attendance_date: moment(e?.Date).format("YYYY-MM-DD"),
          reason: e?.reason ? e?.reason : "",
        };
      });

      // console.log("submitData", submitData);

      importStudentAttendanceFromCSVFile({
        variables: {
          attendanceDate: moment(values?.attendanceDate)?.format("YYYY-MM-DD"),
          classId: classId,
          input: submitData,
        },
      });
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    resetForm,
    setFieldValue,
  } = formik;

  // console.log("errors", errors);

  useEffect(() => {
    resetForm();
    setCSVData([]);
  }, [open]);

  return (
    <Dialog open={open} className="dialog-container" fullWidth maxWidth="sm">
      <DialogTitle sx={{ padding: "10px 15px 10px 20px " }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" justifyContent="center">
            <Typography
              className={language === "en" ? "dialog-title" : "dialog-title-kh"}
            >
              {t("import_csv_file")}
            </Typography>
          </Stack>
          <Stack direction="column" justifyContent="center">
            <IconButton onClick={handleClose}>
              <BiMinusCircle className="close-icon" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("date")}
                </Typography>
                <DatePickerForDialog
                  date={values?.attendanceDate}
                  setDate={(e) => setFieldValue("attendanceDate", e)}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("import")}
                </Typography>
                <Stack direction="row" justifyContent="center">
                  <ImportStudentAttendanceFromCSV
                    setCSVData={setCSVData}
                    open={open}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
      <DialogActions>
        <Grid size={{ xs: 12 }} className="create-container">
          {loading ? (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
            >
              {t("loading")}
            </Button>
          ) : (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
              onClick={handleSubmit}
              // disabled={csvData.length >= 1 ? true : false}
            >
              {t("import")}
            </Button>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
